/**
 * src/components/marketing/utils/utm.js
 *
 * Small attribution helpers used across landing/marketing flows.
 * Stores first-touch click params in localStorage and reuses them
 * when users navigate across internal pages.
 */

const ATTRIBUTION_STORAGE_KEY = "speakable_attribution_v1";
const VISITOR_ID_STORAGE_KEY = "speakable_vid";
const VISITOR_ID_COOKIE_KEY = "speakable_vid";
const ATTRIBUTION_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30; // 30 days
const FBC_COOKIE_MAX_AGE_DAYS = 90;
const FBP_COOKIE_MAX_AGE_DAYS = 90;
const VISITOR_ID_MAX_AGE_DAYS = 365;

const getCookieDomain = () => {
  try {
    const hostname = window?.location?.hostname || "";
    if (hostname === "speakable.online" || hostname.endsWith(".speakable.online")) {
      return ".speakable.online";
    }
  } catch (e) {}
  return "";
};

export const readCookieValue = (name) => {
  if (typeof document === "undefined" || !name) return "";
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : "";
  } catch (e) {
    return "";
  }
};

const setTrackingCookie = (name, value, maxAgeDays) => {
  if (typeof document === "undefined" || !name || !value) return;
  try {
    const parts = [
      `${name}=${encodeURIComponent(String(value))}`,
      "path=/",
      `max-age=${maxAgeDays * 24 * 60 * 60}`,
      "SameSite=Lax",
    ];
    const domain = getCookieDomain();
    if (domain) parts.push(`domain=${domain}`);
    if (typeof window !== "undefined" && window.location?.protocol === "https:") {
      parts.push("Secure");
    }
    document.cookie = parts.join("; ");
  } catch (e) {}
};

const buildFbcValue = (fbclid, creationTime = Date.now()) => {
  const clickId = toSafeString(fbclid);
  return clickId ? `fb.1.${creationTime}.${clickId}` : "";
};

const buildFbpValue = (creationTime = Date.now()) => {
  const randomPart = Math.floor(Math.random() * 1_000_000_000_000);
  return `fb.1.${creationTime}.${randomPart}`;
};

/**
 * Set _fbc first-party cookie from fbclid when Meta Pixel hasn't set it.
 * Format: fb.{subdomainIndex}.{creationTime}.{fbclid}
 * This ensures CAPI can read _fbc even when Pixel is blocked by ad blockers.
 * @param {string} fbclid
 */
const ensureFbcCookie = (fbclid, creationTime = Date.now()) => {
  if (!fbclid || typeof document === "undefined") return;

  try {
    const existing = readCookieValue("_fbc");
    if (existing && existing.endsWith(`.${fbclid}`)) return;

    const fbcValue = buildFbcValue(fbclid, creationTime);
    const maxAge = FBC_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
    const domain = getCookieDomain();
    const parts = [
      `_fbc=${encodeURIComponent(fbcValue)}`,
      "path=/",
      `max-age=${maxAge}`,
      "SameSite=Lax",
    ];
    if (domain) parts.push(`domain=${domain}`);
    if (typeof window !== "undefined" && window.location?.protocol === "https:") {
      parts.push("Secure");
    }
    document.cookie = parts.join("; ");
  } catch (e) {
    // Silently ignore cookie errors (e.g. localhost without matching domain)
  }
};

const ensureFbpCookie = (creationTime = Date.now()) => {
  if (typeof document === "undefined") return "";

  try {
    const existing = readCookieValue("_fbp");
    if (existing) return existing;

    const fbpValue = buildFbpValue(creationTime);
    setTrackingCookie("_fbp", fbpValue, FBP_COOKIE_MAX_AGE_DAYS);
    return fbpValue;
  } catch (e) {
    return "";
  }
};

const hasExplicitMarketingConsent = () => {
  if (typeof window === "undefined" || !window.__cmpConsentState) return true;
  const consent = window.__cmpConsentState;
  return consent.ad_storage === "granted" || consent.personalization_storage === "granted";
};

const clearMetaTrackingCookies = () => {
  if (typeof document === "undefined") return;
  ["_fbc", "_fbp"].forEach((name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=/`;
  });
};

const generateVisitorId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (e) {}
  return `vid_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

export const getOrCreateVisitorId = () => {
  if (typeof window === "undefined" || !window.localStorage) return "";
  try {
    const existing = window.localStorage.getItem(VISITOR_ID_STORAGE_KEY) || readCookieValue(VISITOR_ID_COOKIE_KEY);
    if (existing) {
      window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, existing);
      setTrackingCookie(VISITOR_ID_COOKIE_KEY, existing, VISITOR_ID_MAX_AGE_DAYS);
      return existing;
    }
    const created = generateVisitorId();
    window.localStorage.setItem(VISITOR_ID_STORAGE_KEY, created);
    setTrackingCookie(VISITOR_ID_COOKIE_KEY, created, VISITOR_ID_MAX_AGE_DAYS);
    return created;
  } catch (e) {
    return "";
  }
};

export const readGaClientId = () => {
  const ga = readCookieValue("_ga");
  const parts = ga ? ga.split(".") : [];
  return parts.length >= 4 ? `${parts[2]}.${parts[3]}` : "";
};

export const readGaSessionId = () => {
  if (typeof document === "undefined") return "";
  try {
    const cookies = document.cookie.split(";").map((part) => part.trim());
    const sessionCookie = cookies.find((part) => /^_ga_[A-Z0-9]+=/.test(part));
    if (!sessionCookie) return "";
    const raw = decodeURIComponent(sessionCookie.split("=").slice(1).join("="));
    const parts = raw.split(".");
    return parts.length >= 3 ? parts[2] : "";
  } catch (e) {
    return "";
  }
};

const TRACKING_QUERY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
  "gclid",
  "wbraid",
  "gbraid",
  "ttclid",
  "msclkid",
  "ref",
  "lpv",
];

/**
 * Normalize a value to a safe string.
 * @param {any} value
 * @returns {string}
 */
const toSafeString = (value) => String(value || "").trim();

/**
 * Build URLSearchParams safely.
 * @param {string} locationSearch
 * @returns {URLSearchParams}
 */
const toSearchParams = (locationSearch = "") => {
  try {
    return new URLSearchParams(locationSearch || "");
  } catch (e) {
    return new URLSearchParams();
  }
};

/**
 * Extract supported tracking params from URLSearchParams/search string.
 * @param {string | URLSearchParams} source
 * @returns {Record<string, string>}
 */
const extractTrackingParams = (source = "") => {
  const params = source instanceof URLSearchParams ? source : toSearchParams(source);
  const out = {};

  for (const key of TRACKING_QUERY_KEYS) {
    const value = toSafeString(params.get(key));
    if (value) out[key] = value;
  }

  return out;
};

/**
 * Keep only allowed tracking keys from a persisted object.
 * @param {any} value
 * @returns {Record<string, string>}
 */
const sanitizePersistedParams = (value) => {
  if (!value || typeof value !== "object") return {};
  const out = {};
  for (const key of TRACKING_QUERY_KEYS) {
    const safe = toSafeString(value[key]);
    if (safe) out[key] = safe;
  }
  return out;
};

/**
 * Read persisted attribution payload.
 * @returns {{ params: Record<string, string>, firstPath: string, firstSeenAt: number } | null}
 */
const readPersistedPayload = () => {
  if (typeof window === "undefined" || !window.localStorage) return null;

  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    const updatedAt = Number(parsed.updatedAt || 0);
    if (updatedAt && Date.now() - updatedAt > ATTRIBUTION_MAX_AGE_MS) {
      window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return null;
    }

    return {
      params: sanitizePersistedParams(parsed.params),
      firstPath: toSafeString(parsed.firstPath),
      firstSeenAt: Number(parsed.firstSeenAt || 0),
    };
  } catch (e) {
    return null;
  }
};

/**
 * Read merged attribution (persisted + current location, with current overriding persisted).
 * @param {string} locationSearch
 * @returns {Record<string, string>}
 */
const readMergedAttribution = (locationSearch = "") => {
  const stored = readPersistedPayload()?.params || {};
  const fromLocation = extractTrackingParams(locationSearch);
  return { ...stored, ...fromLocation };
};

/**
 * Persists attribution params from location search.
 * Also stores first landing path/time for diagnostics.
 * @param {string} locationSearch
 * @param {string} pathname
 * @returns {Record<string, string>}
 */
export const persistAttributionFromLocation = (locationSearch = "", pathname = "") => {
  if (typeof window === "undefined" || !window.localStorage) return {};

  try {
    getOrCreateVisitorId();
    const existing = readPersistedPayload();
    const merged = readMergedAttribution(locationSearch);
    const hasAny = Object.values(merged).some(Boolean);

    if (!hasAny && !existing) return {};

    const payload = {
      params: merged,
      firstPath: existing?.firstPath || toSafeString(pathname),
      firstSeenAt: existing?.firstSeenAt || Date.now(),
      updatedAt: Date.now(),
    };
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(payload));

    if (!hasExplicitMarketingConsent()) {
      clearMetaTrackingCookies();
      return { ...merged, fbclid: "" };
    }

    // Set _fbc cookie from fbclid so CAPI works even when Pixel is blocked
    if (merged.fbclid) {
      ensureFbcCookie(merged.fbclid, payload.firstSeenAt);
    }
    ensureFbpCookie(payload.firstSeenAt);

    return merged;
  } catch (e) {
    return {};
  }
};

/**
 * Read current referral code from URL or persisted attribution.
 * @param {string} locationSearch
 * @returns {string}
 */
export const readRefCodeFromAttribution = (locationSearch = "") => {
  const merged = readMergedAttribution(locationSearch);
  return toSafeString(merged.ref);
};

/**
 * Append stored/current attribution params to an internal path.
 * Existing params on target path win over stored ones.
 * @param {string} to
 * @param {string} locationSearch
 * @returns {string}
 */
export const appendAttributionToPath = (to = "", locationSearch = "") => {
  const target = toSafeString(to);
  if (!target || target.startsWith("#")) return target;

  // Do not modify absolute/protocol links.
  if (/^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(target)) return target;

  const hashIndex = target.indexOf("#");
  const hash = hashIndex >= 0 ? target.slice(hashIndex) : "";
  const withoutHash = hashIndex >= 0 ? target.slice(0, hashIndex) : target;

  const queryIndex = withoutHash.indexOf("?");
  const pathname = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const query = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : "";

  const targetParams = new URLSearchParams(query);
  const attribution = readMergedAttribution(locationSearch);

  for (const key of TRACKING_QUERY_KEYS) {
    const value = toSafeString(attribution[key]);
    if (value && !targetParams.has(key)) {
      targetParams.set(key, value);
    }
  }

  const nextQuery = targetParams.toString();
  return `${pathname}${nextQuery ? `?${nextQuery}` : ""}${hash}`;
};

/**
 * Read UTM object used by marketing payloads (from location or persisted attribution).
 * @param {string} locationSearch
 * @returns {{ source: string, medium: string, campaign: string, content: string, term: string } | null}
 */
export const readUtmFromLocation = (locationSearch = "") => {
  try {
    const merged = readMergedAttribution(locationSearch);
    const utm = {
      source: toSafeString(merged.utm_source),
      medium: toSafeString(merged.utm_medium),
      campaign: toSafeString(merged.utm_campaign),
      content: toSafeString(merged.utm_content),
      term: toSafeString(merged.utm_term),
    };
    const any = Object.values(utm).some(Boolean);
    return any ? utm : null;
  } catch (e) {
    return null;
  }
};

/**
 * Read click IDs object from URL or persisted attribution.
 * @param {string} locationSearch
 * @returns {{ fbclid: string, gclid: string, wbraid: string, gbraid: string, ttclid: string, msclkid: string } | null}
 */
export const readClickIdsFromLocation = (locationSearch = "") => {
  try {
    const merged = readMergedAttribution(locationSearch);
    const clickIds = {
      fbclid: toSafeString(merged.fbclid),
      gclid: toSafeString(merged.gclid),
      wbraid: toSafeString(merged.wbraid),
      gbraid: toSafeString(merged.gbraid),
      ttclid: toSafeString(merged.ttclid),
      msclkid: toSafeString(merged.msclkid),
    };
    const any = Object.values(clickIds).some(Boolean);
    return any ? clickIds : null;
  } catch (e) {
    return null;
  }
};

/**
 * Return complete attribution snapshot suitable for backend payloads.
 * @param {string} locationSearch
 * @returns {{
 *   utm: {source: string, medium: string, campaign: string, content: string, term: string} | null,
 *   clickIds: {fbclid: string, gclid: string, wbraid: string, gbraid: string, ttclid: string, msclkid: string} | null,
 *   ref: string,
 *   lpv: string,
 *   landingPath: string,
 *   firstSeenAt: string | null
 * }}
 */
export const readAttributionSnapshot = (locationSearch = "") => {
  const hashQuery = typeof window !== "undefined" ? window.location.hash.split("?")[1] : "";
  const effectiveSearch = locationSearch || (hashQuery ? `?${hashQuery}` : "");
  const merged = readMergedAttribution(effectiveSearch);
  const persisted = readPersistedPayload();
  const utm = readUtmFromLocation(effectiveSearch);
  const marketingConsent = hasExplicitMarketingConsent();
  const clickIds = readClickIdsFromLocation(effectiveSearch);

  const firstSeenMs = Number(persisted?.firstSeenAt || 0);
  const firstSeenAt =
    firstSeenMs > 0 && Number.isFinite(firstSeenMs)
      ? new Date(firstSeenMs).toISOString()
      : null;
  const creationTime = firstSeenMs > 0 && Number.isFinite(firstSeenMs) ? firstSeenMs : Date.now();
  if (!marketingConsent) clearMetaTrackingCookies();
  const fbclid = marketingConsent ? toSafeString(clickIds?.fbclid || merged.fbclid) : "";
  if (fbclid) ensureFbcCookie(fbclid, creationTime);
  const fbc = marketingConsent ? readCookieValue("_fbc") || buildFbcValue(fbclid, creationTime) : "";
  const fbp = marketingConsent ? readCookieValue("_fbp") || ensureFbpCookie(creationTime) : "";

  return {
    utm,
    clickIds: marketingConsent ? { ...clickIds, fbclid } : { ...clickIds, fbclid: "" },
    fbc: toSafeString(fbc),
    fbp: toSafeString(fbp),
    fbclid,
    gaClientId: readGaClientId(),
    gaSessionId: readGaSessionId(),
    visitorId: getOrCreateVisitorId(),
    sourceUrl: typeof window !== "undefined" ? window.location.href : "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    consent: typeof window !== "undefined" ? window.__cmpConsentState || null : null,
    ref: toSafeString(merged.ref),
    lpv: toSafeString(merged.lpv),
    landingPath: toSafeString(persisted?.firstPath),
    firstSeenAt,
  };
};

